// A part-rig bundle on the next path: decode the neutral pose, present, recolour on a settled view.
// Notes: ~/comments/aos-threejs-poc/PartRigAvatar_start_and_teardown.md

import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useFrame, useThree } from '@react-three/fiber'
import { getPluginNode } from '../../lib/pluginNodeRegistry.js'
import { resolveProviders } from '../../lib/ortSession.js'
import { setContentFramer, fitCameraToSphere } from '../../lib/viewportFraming.js'
import { reportDowngrade, reportPrecision } from '../../lib/ogsAssetStatus.js'
import * as ogsOrt from '../../ogs/inference/ort.ts'
import { ogsGpuFlags } from '../../ogs/inference/ogsGpuFlags.ts'
import { chooseLift } from '../../ogs/inference/myra/myraLiftGate.ts'
import { applyMomentScale } from '../../ogs/inference/plucker.ts'
import { resolveWorldRotation, worldRotationQuaternion } from '../../ogs/render/worldRotation.ts'
import { SplatMeshUpdater } from '../../ogs/render/SplatMeshUpdater.ts'
import { resolveAvatarTransformTarget } from '../../lib/avatarTransformTarget.js'
import { LocalFrameCamera } from '../../ogs/render/localFrameCamera.ts'
import { createCameraWatch } from '../../ogs/cameraWatch.ts'
import { useLightRelight } from './useLightRelight.js'
import { bootBranches, neutralPositions } from '../../ogs/pipeline/bootBranches.ts'
import { createDrivenLifters } from '../../ogs/pipeline/drivenLifter.ts'
import { buildPartDrive } from '../../ogs/pipeline/partDriveBuilder.ts'
import { armSyntheticFace } from '../../ogs/pipeline/syntheticFace.ts'
import { createBranchMeshOverlay } from '../../ogs/render/branchMeshOverlay.js'
import { mountSplatOverlay } from '../../ogs/render/splatOverlayMount.js'
import { partDriveRegistry } from '../../ogs/preview/partDriveRegistry.js'
import { partRigUrl, PART_DRIVER_RIG } from '../../ogs/inference/partDriver.js'
import { setSplatEyeFollowCapability } from '../../lib/eyeFollowSupport.js'
import { useSplatFaceDrive } from './useSplatFaceDrive.js'
import BodyDrive, { SplatMovementTarget } from './BodyDrive.jsx'
import { neutralBounds } from '../../ogs/pipeline/framing.ts'
import { PartRigPipeline } from '../../ogs/pipeline/PartRigPipeline.ts'
import { resolveFaceDrivePlan, describeFaceDrivePlan } from '../../ogs/pipeline/faceDrivePlan.ts'
import { publishDebug, retractDebug } from '../../ogs/pipeline/ogsDebug.ts'
import { useAvatarRoot } from './AvatarRoot.jsx'
import { useRenderPlan } from './useRenderPlan.js'
import SplatSceneMesh from '../SplatSceneMesh.jsx'

const RENDERER_NODE_TYPE = 'plugin.ogs.renderer'

/** The terminal render node, from the plugin REGISTRY — core never imports plugins/. */
function createRendererNode(options) {
  const create = getPluginNode(RENDERER_NODE_TYPE)?.instance?.create
  if (typeof create !== 'function') {
    throw new Error(`the ${RENDERER_NODE_TYPE} plugin is not registered — nothing can present splats`)
  }
  return create(options)
}

export default function PartRigAvatar({ boot, slug, log, animationBuffer, onReady, onError, onRenderBlocked }) {
  const [splatDriver, setSplatDriver] = useState(null)
  const gl = useThree((state) => state.gl)
  const scene = useThree((state) => state.scene)
  const camera = useThree((state) => state.camera)
  const root = useAvatarRoot()

  // Two instances: a pass mid-await must never read the watch's own update.
  const localCam = useMemo(() => new LocalFrameCamera(), [])
  const watchCam = useMemo(() => new LocalFrameCamera(), [])
  const watch = useRef(null)
  if (!watch.current) watch.current = createCameraWatch()

  const pipeline = useRef(null)
  const started = useRef(false)
  // Read by the per-frame face hook; all set asynchronously after mount.
  const face = useRef(null)
  const drivePlan = useRef(null)
  const regions = useRef(null)
  const planRef = useRef(null)
  const live = useRef({ cancelled: false, branches: null, updater: null, framer: null, avatarWasVisible: null, face: null, overlay: null })

  /** SyntheticOGSFace: the ARKit frame poses the part rig's facial joints. */
  const armFace = useCallback(async ({ engine, packs, poser, state }) => {
    const sceneRoot = poser.rig?.group
    if (!sceneRoot) {
      publishDebug('__OGS_SYNTHETIC_FACE__', {
        state: 'off', matched: 0, packSource: null, reason: 'the part rig exposes no scene group',
      })
      return
    }
    const handle = await armSyntheticFace({
      fetchBytes: packs.fetchBytes,
      sceneRoot,
      // Head-only present; a body drive that owns the rig carries the bones instead.
      onPosed: () => { if (!engine.driveFace()) engine.driveAt(engine.landedTime ?? 0) },
      log,
    })
    if (!handle) {
      publishDebug('__OGS_SYNTHETIC_FACE__', {
        state: 'off', matched: 0, packSource: null, reason: 'no orl_pack.bin for this character',
      })
      return
    }
    if (state.cancelled) { handle.release(); return }
    face.current = handle
    state.face = handle
    publishDebug('__OGS_SYNTHETIC_FACE__', {
      state: 'armed', matched: handle.matched, packSource: handle.packSource, reason: null,
    })
  }, [log])

  /** The bundle's rig.glb, registered LAZILY: whoever poses the body pays for the load. */
  const registerPartDrive = useCallback(({ engine, packs, scn, branches, worldRotation, worldScale, drive, state, device, isLive }) => {
    const rigUrl = partRigUrl((name) => !!packs.getBytes(name), packs.texBase)
    if (!rigUrl) { log.info(`no ${PART_DRIVER_RIG} in this bundle — no body rig to pose`); return }

    const build = async () => {
      const poser = await buildPartDrive({
        rigUrl,
        branchMeshes: Object.fromEntries(scn.branches.map((branch) => [branch.name, branch.mesh])),
        branches,
        worldRotation,
        worldScale,
        // The device the live branches are SKINNED on. Without it every live
        // branch keeps the rigid anchor, which separates the head at the collar
        // the moment the character walks — `renderContract.js` reports that
        // rather than leaving it to be seen.
        device: device ?? null,
        // A branch whose own deform produces its verts, so the body SKINS it
        // rather than carrying it rigidly. Required — see buildPartDrive.
        isLive,
        isCancelled: () => state.cancelled,
        log,
      })
      if (!poser || state.cancelled) return null
      engine.setPoser(poser)
      // The part rig's facial joints as the head's vertex source, when the plan asks.
      if (drive.synthetic.enabled) await armFace({ engine, packs, poser, state })
      return {
        driveAt: (t) => engine.driveAt(t),
        driveFace: () => engine.driveFace(),
        duration: poser.duration,
        rig: poser.rig,
        rigToWorld: poser.rigToWorld,
        rigToWorldScale: poser.rigToWorldScale,
        posedTime: () => engine.landedTime,
      }
    }
    // Never after teardown: a late registration hands the next character a builder
    // over this disposed scene.
    if (state.cancelled) return
    partDriveRegistry.register({ build })
  }, [armFace, log])

  const startPipeline = useCallback(async (plan) => {
    const state = live.current
    const { packs, scene: scn } = boot
    const flags = ogsGpuFlags()
    const manifest = scn.manifest
    const worldScale = manifest.worldScale
    const worldOffset = manifest.worldOffset
    const rotationDeg = resolveWorldRotation(manifest.worldRotation)
    const worldRotation = worldRotationQuaternion(rotationDeg)
    const momentScale = applyMomentScale(manifest.momentScale)
    log.info(`momentScale=${momentScale} worldScale=${worldScale} `
      + `worldOffset=[${worldOffset.join(', ')}] worldRotation=[${rotationDeg.join(', ')}]`)

    const providers = await resolveProviders(flags.ep, { preferWebgpu: true })
    if (state.cancelled) return
    const liftChoice = chooseLift({ subject: manifest.subject, character: slug }, flags.myraLift)
    log.info(`${liftChoice.impl} lift (${liftChoice.reason})`)

    const booted = await bootBranches({
      ort: ogsOrt,
      providers,
      flags,
      scene: scn,
      packs,
      precision: plan.precision,
      lift: liftChoice.impl,
      // PACKED: every branch writes three's splat buffers on the device
      // (`setThreeTargets`) and a present is the lifts being SUBMITTED, nothing
      // more. This was `false` so the body drive's over-stretch cull could read
      // the SoA fields on the CPU — and that one CPU pass is what made the whole
      // path read every splat back each pose. Measured on myra-pbr: 212 ms of a
      // 276 ms pose, the avatar updating 3.6x/s while the page drew at 20 fps,
      // every region several frames stale and the head worst. The cull now runs
      // in `lift_pass2.wgsl`, where the values already live, so nothing crosses
      // to the CPU and the trade is gone rather than chosen.
      packed: true,
      worldScale,
      worldOffset,
      worldRotation,
      onDowngrade: reportDowngrade,
      onBranches: (branches) => { state.branches = branches },
      isCancelled: () => state.cancelled,
      log,
    })
    if (!booted || state.cancelled) return
    reportPrecision({
      requested: plan.precision,
      geom: booted.branches[0]?.geomUsedFp16 ? 'fp16' : 'fp32',
      appr: booted.branches[0]?.apprUsedFp16 ? 'fp16' : 'fp32',
    })

    // The face drive is published for diagnosis; nothing consumes it until phase 4.
    const drive = resolveFaceDrivePlan({
      manifest, rig: packs.rigPlan?.rig ?? 'none', liveBranches: [], graph: plan,
    })
    publishDebug('__OGS_FACE_DRIVE__', drive)
    drivePlan.current = drive
    // A channel group drives only if BOTH the bundle and the blueprint allow it. The
    // bundle declines what its capture never contained; the blueprint is the author's say.
    const declared = manifest.faceDrives ?? {}
    const asked = plan.faceDrive.liveMask
    regions.current = {
      brow: (declared.brow ?? true) && asked.brow,
      eye: (declared.eye ?? true) && asked.eye,
      cheek: (declared.cheek ?? true) && asked.cheek,
      nose: (declared.nose ?? true) && asked.nose,
    }
    for (const line of describeFaceDrivePlan(drive)) log.info(line)

    const updater = new SplatMeshUpdater(root)
    updater.meshScale = worldScale
    // Must match the offset the plücker origin was computed against, and the rotation
    // the branches got INVERTED, or the shading is conditioned in a frame the mesh is not in.
    updater.meshOffset = worldOffset
    updater.meshRotation = worldRotation
    state.updater = updater

    // Frame BEFORE the first appearance pass: appr's plücker is view-dependent and a
    // degenerate boot camera silently corrupts the colour of every surface facing away.
    const frame = (preserveDirection) => {
      const bounds = neutralBounds(neutralPositions(booted.branches), { worldScale, worldOffset, worldRotation })
      if (!bounds) return false
      return fitCameraToSphere(camera, window.__ORBIT_CONTROLS__, bounds.centre, bounds.radius, { preserveDirection })
    }
    frame(false)
    state.framer = setContentFramer(() => frame(true))

    const glbAvatar = window.__AVATAR_SCENE__
    if (glbAvatar) { state.avatarWasVisible = glbAvatar.visible; glbAvatar.visible = false }

    // The splats are drawn straight out of the lift's buffers, by the APP's own
    // renderer, in the app's own scene: the present is EXTERNAL, a frame is the
    // lift submitting and nothing else. Without one there is no GPU lift either,
    // so the CPU lifter feeds the updater's own GaussianSplat instead.
    //
    // `renderer` and `scene` are the whole contract now — the mount used to take
    // the CANVAS to sit a second renderer over. Passing the old argument here
    // does not fail loudly: the mesh build throws inside the mount, which
    // reports and returns null, and this path quietly falls back to the packed
    // present with a readback per frame. It is also what `pace` below waits on,
    // so the stall would be the first symptom rather than the slowness.
    const overlay = await mountSplatOverlay({
      device: booted.device, branches: booted.branches, camera,
      renderer: gl, scene, avatarRoot: root,
      worldScale, worldOffset, worldRotation,
      isCancelled: () => state.cancelled, log,
    })
    if (state.cancelled) { overlay?.dispose(); updater.dispose(); return }
    state.overlay = overlay
    setSplatDriver(overlay?.bridge ?? null)
    // The updater's mesh only exists on the CPU-lift path; with an overlay it
    // has no data and must not be drawn on top of what the overlay renders.
    if (overlay) updater.setVisible(false)

    // ?meshview — THE SPLAT-FREE VIEW OF THE SAME POSE. It draws each branch's
    // faces over the POSED VERTICES the lift anchors to, so it shows what the
    // pose path produced with no decoder, no lift and no present in the way.
    // That is the one instrument that separates "the head is posed late" from
    // "the head is posed on time and PRESENTED late": with `both`, the mesh is
    // the pose and the splats are the present, and any gap between them on
    // screen IS the pipeline's latency, measured by eye at whatever the frame
    // rate happens to be.
    //
    // It existed only on the legacy path (`OGSModel.jsx`), while this path
    // parsed a per-part `meshView` into the render plan and honoured it nowhere
    // — so the flag looked available on every character and did nothing on the
    // ones that run here. Updated per rendered frame rather than per pose,
    // deliberately: the point is to see the mesh MOVE while the splats have not
    // caught up, which an update tied to the pose would hide.
    const meshFlags = ogsGpuFlags()
    if (meshFlags.meshView !== 'off') {
      const meshOverlay = createBranchMeshOverlay(root, booted.branches, {
        scale: worldScale, offset: worldOffset, rotation: worldRotation,
        wireframe: meshFlags.meshView !== 'solid',
      })
      if (!meshOverlay) {
        log.error('?meshview: no branch carries a face list — nothing to draw')
      } else {
        state.meshOverlay = meshOverlay
        let raf = 0
        const tick = () => { meshOverlay.update(); raf = requestAnimationFrame(tick) }
        raf = requestAnimationFrame(tick)
        state.meshOverlayStop = () => cancelAnimationFrame(raf)
        log.info(`?meshview=${meshFlags.meshView}: ${meshOverlay.count} branch meshes over the posed verts`)
      }
    }

    // The renderer's sources take WRAPPED lifters: a driven frame holds the decoder
    // outputs (no UV re-upload) and culls the splats a foreign clip breaks.
    const lifters = createDrivenLifters(booted.branches)
    const renderer = createRendererNode({
      id: 'renderer',
      updater,
      present: overlay ? 'external' : 'packed',
      sources: booted.branches.map((branch, index) => ({
        name: branch.name,
        ports: branch.outputs.map((port) => port.name),
        lifter: lifters.lifterFor(branch, index),
        getVerts: () => branch.verts,
        capacity: branch.uvRes * branch.uvRes,
      })),
    })
    await renderer.init(booted.rendererCtx)
    if (state.cancelled) { updater.dispose(); return }

    const engine = new PartRigPipeline({
      branches: booted.branches,
      renderer,
      lifters,
      camera: () => localCam.update(camera, root),
      // With an overlay the updater's mesh stays hidden whatever the gate says —
      // the overlay is what draws. Same rule as the legacy path.
      setVisible: (visible) => updater.setVisible(overlay ? false : visible),
      // Backpressure: with no readback nothing else bounds the GPU queue.
      pace: overlay ? () => overlay.renderer.nextFrame() : null,
      isActive: () => !state.cancelled,
      log,
      worldScale,
    })
    pipeline.current = engine
    publishDebug('__OGS_PIPELINE__', engine.debug())
    engine.applyPlan(plan, null)
    const ready = await engine.init()
    if (state.cancelled) return
    engine.setVisible(true)
    publishDebug('__OGS_PIPELINE__', engine.debug())
    for (const line of ready.lines) log.info(line)
    onReady?.()

    // This pipeline renders a static neutral until something poses the rig, so a
    // rig-level eye aim has nothing to ride. Said on the node rather than sitting mute.
    setSplatEyeFollowCapability({
      route: 'none',
      reason: 'Not supported on this splat bundle — its pipeline decodes once at the neutral '
        + 'pose, so there is no live rig drive for the eye aim to ride.',
    })

    registerPartDrive({
      engine, packs, scn, branches: booted.branches, worldRotation, worldScale, drive, state,
      // THE LIFT DEVICE, so the skinning lands in the very buffer the jacobians
      // read. It was `booted.sharedDevice`, which went null whenever the decoder
      // IO was off — so a device that was right there read as absent and every
      // live branch stayed on its rigid anchor, silently.
      device: booted.device,
      // The pipeline's own test, never a local one: the part drive and the body
      // skinner asking the same question two ways is how they disagree.
      isLive: (br) => engine.isLive(br),
    })
  }, [boot, camera, gl, localCam, log, onReady, registerPartDrive, root, scene, slug])

  const onPlan = useCallback((next, prev) => {
    planRef.current = next
    if (!started.current) {
      if (next.phase !== 'rendering') return
      started.current = true
      log.info('OGS Avatar wired — starting the render')
      startPipeline(next).catch((error) => {
        log.error('init failed', error)
        if (!live.current.cancelled) onError?.(error)
      })
      return
    }
    pipeline.current?.applyPlan(next, prev)
    pipeline.current?.setVisible(next.phase === 'rendering')
    if (pipeline.current) publishDebug('__OGS_PIPELINE__', pipeline.current.debug())
  }, [log, onError, startPipeline])

  useRenderPlan({ onPlan, onBlocked: onRenderBlocked, started: () => started.current })

  // The face graph reaches the splat here. Composed every frame even with no face
  // handle yet, so the graph, the editor's weight bars and the A2F buffer keep running.
  useSplatFaceDrive({
    animationBuffer,
    getFace: () => face.current,
    getRegions: () => regions.current,
    active: () => !!pipeline.current && !live.current.cancelled,
  })

  // appr is conditioned on a plücker ray, so a camera move has to re-run it. Compared in
  // the AVATAR's frame: the root turning under a still camera is a view change too.
  useFrame(() => {
    const engine = pipeline.current
    if (!engine) return
    if (watch.current.movedLocal(watchCam.update(camera, root))) engine.runAppearance()
  })

  // The light is the third trigger, and a relight rather than a re-decode.
  useLightRelight({
    active: () => !!pipeline.current?.followsLight,
    avatar: () => resolveAvatarTransformTarget(),
    onChanged: () => pipeline.current?.relight(),
  })

  useEffect(() => {
    const state = live.current
    state.cancelled = false
    return () => {
      state.cancelled = true
      setSplatDriver(null)
      // A reported block belongs to the character being left, never the next one.
      onRenderBlocked?.(null)
      state.framer?.()
      state.framer = null
      // A registration left standing would hand the next character a builder over
      // this disposed scene.
      partDriveRegistry.reset()
      state.face?.release()
      state.face = null
      face.current = null
      drivePlan.current = null
      setSplatEyeFollowCapability(null)
      pipeline.current?.dispose()
      pipeline.current = null
      // Owns its canvas, its bridge and the globals it published.
      state.overlay?.dispose()
      state.overlay = null
      // The rAF loop FIRST: it reads `branch.verts` every frame, and the branches
      // are disposed a few lines below. A loop left running over a disposed
      // branch is the quiet kind of leak that survives a character switch.
      state.meshOverlayStop?.()
      state.meshOverlayStop = null
      state.meshOverlay?.dispose()
      state.meshOverlay = null
      watch.current?.reset()
      for (const branch of state.branches ?? []) {
        try { branch.dispose() } catch { /* best-effort: a leaked session beats a thrown teardown */ }
      }
      state.branches = null
      state.updater?.dispose()
      state.updater = null
      const glbAvatar = window.__AVATAR_SCENE__
      if (glbAvatar && state.avatarWasVisible !== null) glbAvatar.visible = state.avatarWasVisible
      state.avatarWasVisible = null
      retractDebug('__OGS_PIPELINE__')
      retractDebug('__OGS_FACE_DRIVE__')
      retractDebug('__OGS_SYNTHETIC_FACE__')
    }
  }, [onRenderBlocked, scene])

  return (
    <>
      {/* The mesh's two moments in the app's frame: the per-frame compute before
          the render, and the present waiters after it — `pace` above is awaiting
          them, so without this the pipeline stalls after one pose. */}
      <SplatSceneMesh driver={splatDriver} />
      <BodyDrive slug={slug} getPlan={() => planRef.current} log={log} />
      <SplatMovementTarget />
    </>
  )
}
